import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { loadProjects } from './data/projects';

/**
 * Smistamento per percorso (PIANO.md §10).
 *
 * Tre destinazioni: il sito pubblico, l'area riservata, la pagina di un link
 * privato. Lo smistamento avviene qui, prima di montare qualsiasi cosa, per due
 * motivi:
 *
 *   - App.tsx non va toccato. La navigazione interna del sito non cambia mai
 *     l'indirizzo, quindi basta guardare il percorso di partenza.
 *   - Admin e pagine private stanno in pacchetti separati, caricati solo se
 *     servono: chi visita il portfolio non ne scarica una riga (PIANO.md §8).
 *
 * Sul server, /admin e /p/<token> arrivano a index.html grazie alla regola di
 * rewrite nel .htaccess: senza, Apache risponderebbe 404 prima ancora che il
 * JavaScript entri in gioco.
 */
const AdminApp = React.lazy(() => import('./admin/AdminApp'));
const LinkView = React.lazy(() => import('./p/LinkView'));

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

const attesa = (
  <div
    style={{
      minHeight: '100vh',
      background: '#000',
      color: '#666',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'Syne', system-ui, sans-serif",
      fontSize: '0.75rem',
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
    }}
  >
    caricamento
  </div>
);

const disegna = (contenuto: React.ReactNode) =>
  root.render(<React.StrictMode>{contenuto}</React.StrictMode>);

const percorso = window.location.pathname.replace(/\/+$/, '') || '/';
const linkPrivato = percorso.match(/^\/p\/([A-Za-z0-9_-]{8,64})$/);

/**
 * Il sito nasconde il cursore di sistema (index.css) perche' ne disegna uno suo
 * con CustomCursor. Le pagine che non montano quel componente resterebbero
 * senza alcun cursore: qui si dice al foglio di stile di lasciare stare.
 *
 * Nella fase 05 la pagina di un link privato montera' gli stessi componenti del
 * sito pubblico, CustomCursor compreso: da li' in poi andra' tolta da questa
 * riga, altrimenti si ritroverebbe due cursori.
 */
const cursoreDiSistema = () => {
  document.documentElement.setAttribute('data-area', 'riservata');
};

if (percorso === '/admin' || percorso.startsWith('/admin/')) {
  // L'admin legge i dati dall'API, non dallo snapshot: niente da precaricare.
  cursoreDiSistema();
  disegna(<Suspense fallback={attesa}><AdminApp /></Suspense>);
} else if (linkPrivato) {
  // Niente cursoreDiSistema() qui: la pagina di un link decide da sola, perche'
  // ha due facce. Con la galleria aperta monta gli stessi componenti del sito,
  // CustomCursor compreso, e marcandola come area di lavoro si vedrebbero due
  // cursori sovrapposti; nelle schermate di servizio - password, attesa, link
  // scaduto - CustomCursor non c'e' e serve invece quello di sistema.
  disegna(<Suspense fallback={attesa}><LinkView token={linkPrivato[1]} /></Suspense>);
} else {
  // Sito pubblico. Lo snapshot e' gia' in arrivo dal preload in index.html: qui
  // si aspetta solo che sia pronto, cosi' i componenti restano sincroni e non
  // c'e' nessun salto di layout al primo render. Se non arriva, si disegna lo
  // stesso.
  loadProjects().finally(() => disegna(<App />));
}
